import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { TranslationProvider } from './utils/translations/TranslationProvider';
import { initAutoIntel } from './utils/autointel';
import {
  DiscoverProvider,
  IStaticOptions,
  IDynamicOptions,
} from '@adloid-technologies/discover-sdk';
import { metadata, uimetadata } from './utils/metadata';
import { loadAllExternalScripts } from './utils/loadAllExternalScripts';
import { ExternalAnalyticsGM } from './utils/externalAnalyticsGM';
import { getMode, getVersion } from './utils/globals';
import { AnalyticsSegmentationProvider } from './utils/useAnalytics';
import { initSentry, Sentry } from './utils/sentry';

initSentry();
import { getLanguageParameter } from './utils/common';

// Apply CSS variables from styleConfig (font families, colors, etc.) to :root
// so all components can consume var(--default-font-family), var(--bold-font-family), etc.
if (uimetadata?.styleConfig?.cssVariables) {
  const root = document.documentElement;
  for (const [key, value] of Object.entries(uimetadata.styleConfig.cssVariables)) {
    root.style.setProperty(key, value as string);
  }
}

// Initialize AutoIntel SDK asynchronously (non-blocking)
initAutoIntel().catch((err) => {
  console.warn('[AutoIntel] SDK init skipped:', err?.message);
  Sentry.captureException(err);
});

// ── External scripts + Adobe analytics ──────────────────────────────────────
// Ported from 1.0's src/index.tsx. Every linkId in this repo is a GM project,
// so ExternalAnalyticsGM is the only implementation needed here (1.0 also
// branches to Nissan/Gravite classes for linkIds that don't exist in 2.0).
//
// Order matters: the externalScript block injects Adobe Launch (and the consent
// /responsive-iframe scripts), and analytics init has to run after that so
// window._satellite exists. A third-party script failing must never block init,
// hence the same .then/.catch pair 1.0 uses.
const initExternalAnalytics = () => {
  try {
    const cfg = (uimetadata as any)?.config ?? {};
    const modelName: string | undefined = cfg.model;
    const analytics = new ExternalAnalyticsGM();
    if (modelName) {
      analytics.init(cfg.externalAnalytics, modelName, cfg);
    }
    analytics.addInitialEvars();
    // Presentation page-load beacon; eVars are set by addInitialEvars above.
    window.externalAnalyticsGM?.presentationPageLoad?.();
  } catch (err) {
    console.warn('[analytics] init skipped:', (err as Error)?.message);
    Sentry.captureException(err);
  }
};

loadAllExternalScripts((uimetadata as any)?.externalScript?.[getMode()] || {})
  .then(initExternalAnalytics)
  .catch(initExternalAnalytics);

// Release channel resolution (highest precedence first):
//   1. VITE_RELEASE_CHANNEL env var — per-deploy override.
//   2. The loaded project's config.releaseChannel — per-project pin (e.g.
//      Corvette is alpha-only, so it must request alpha in EVERY environment,
//      including the deployed build; otherwise the graphics engine throws
//      "Not able to resolve releaseId").
//   3. Fallback: the build's own mode, via getVersion() — dev → alpha,
//      staging → beta, main → gamma, matching 1.0's getVersion() in
//      src/utils/globals.ts. This used to be `import.meta.env.DEV ? alpha :
//      gamma`, but DEV is only true under `vite serve`, so every deployed
//      build — the dev links included — asked for gamma.
// Projects that don't set config.releaseChannel keep the old behaviour, so
// previously added linkIds are unaffected.
const releaseChannel: 'alphaVersion' | 'betaVersion' | 'gammaVersion' =
  (import.meta.env.VITE_RELEASE_CHANNEL as
    | 'alphaVersion'
    | 'betaVersion'
    | 'gammaVersion'
    | undefined) ||
  ((uimetadata as any)?.config?.releaseChannel as
    | 'alphaVersion'
    | 'betaVersion'
    | 'gammaVersion'
    | undefined) ||
  getVersion();

const staticOptions: IStaticOptions = {
  useKTXTextures: false,
  releaseChannel,
};

const dynamicOptions: IDynamicOptions = {
  // When deferredAssetLoading=true: don't load assets on startup (loadAssets() called on Enter click)
  // When deferredAssetLoading=false: load assets immediately so Enter button waits for them
  enableAssets: !(uimetadata.plugins?.deferredAssetLoading ?? false),
};

// Derive model and default variant from UI metadata
const modelId: string = (uimetadata as any).config?.model || 'Traverse';
const variantMap = (uimetadata.models as any)?.[modelId]?.variants ?? {};
const defaultVariantId: string =
  (
    Object.entries(variantMap).find(([, v]: any) => v?.isDefault) as
    | [string, any]
    | undefined
  )?.[0] ??
  Object.keys(variantMap)[0] ??
  '';

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <TranslationProvider defaultLanguage={getLanguageParameter()}>
      <DiscoverProvider
        iframeId="gsdk"
        metadata={metadata}
        defaultConfiguration={{
          modelId,
          variantID: defaultVariantId,
          view: { type: 'exterior', hotspotId: 'exteriorCamera' },
        }}
        staticOptions={staticOptions}
        dynamicOptions={dynamicOptions}
      >
        <AnalyticsSegmentationProvider>
          <Sentry.ErrorBoundary
            fallback={() => (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: '#000',
                  color: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  textAlign: 'center',
                  padding: '0 32px',
                }}
              >
                <p style={{ fontSize: '18px', fontWeight: 700 }}>Something went wrong</p>
                <p style={{ fontSize: '14px', opacity: 0.6 }}>Please refresh the page to try again.</p>
              </div>
            )}
          >
            <App />
          </Sentry.ErrorBoundary>
        </AnalyticsSegmentationProvider>
      </DiscoverProvider>
    </TranslationProvider>
  </StrictMode>,
);
